/**
 * FeedRotator — page cursor with tail-wraparound so pages are always full.
 *
 * Port of apps/signage-tv/lib/rendering/feed_rotator.dart. Pure; the DOM
 * renderer owns the timer and calls advance() on each tick.
 *
 * Spec: signage-device-app-spec §3.3
 */
export class FeedRotator {
  readonly pageSize: number;
  private _page = 0;

  constructor(pageSize: number) {
    if (pageSize <= 0) {
      throw new Error(`FeedRotator pageSize must be > 0 (got ${pageSize})`);
    }
    this.pageSize = pageSize;
  }

  get page(): number {
    return this._page;
  }

  /**
   * Items visible on the current page. If items.length <= pageSize, the
   * full list is returned and the cursor is a no-op.
   */
  currentPage<T>(items: ReadonlyArray<T>): T[] {
    if (items.length <= this.pageSize) return [...items];
    const start = (this._page * this.pageSize) % items.length;
    const end = start + this.pageSize;
    if (end <= items.length) return items.slice(start, end);
    // Wrap: splice tail + head so the caller always gets a full page.
    return [...items.slice(start), ...items.slice(0, end - items.length)];
  }

  pageCount(itemCount: number): number {
    if (itemCount <= this.pageSize) return 1;
    return Math.ceil(itemCount / this.pageSize);
  }

  /**
   * Advance to the next page. Returns true if the cursor actually moved
   * (false when only one page exists).
   */
  advance(itemCount: number): boolean {
    const total = this.pageCount(itemCount);
    if (total <= 1) {
      this._page = 0;
      return false;
    }
    this._page = (this._page + 1) % total;
    return true;
  }

  reset(): void {
    this._page = 0;
  }
}
